import React, { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../../app/helpers";
import { RootState } from "../../../../app/rootReducer";
import { createCompany } from "../../../../utils/axios-requests";
import { createFormMetadata, getDefaultMetaDataForCreate } from "./createFormMetadata.function";
import { useRemoteSetMetadata } from "../../../../hooks/Remote/Metadata/useRemoteSetMetadata";
import { CompanyCardSettingContent } from "../../CompanyCard/SettingDialog/CompanyCard-Setting-Content.partial";
import { CheckinPolicyGroup } from "../../CompanyCard/CompanyCard.types";
import { CheckinAndMaxAdvancedBooking } from "../../../Login/typings/login.types";
import {
  filterEnabledPolicy,
  generateAdvancedBookingSettings
} from "../../functions/company-card.component.functions";
import { BookingType } from "../../../Booking-Form/typings/booking-inputs";
import { useRemoteFetchMetadata } from "../../../../hooks/Remote/Metadata/useRemoteFetchMetadata";
import { FileObject } from "../../../FileDropzone/typings/FileObject.type";
import { useSnackbar } from "notistack";
import {
  buildIndependentBookingSettingsPayload,
  getVisibleIndependentBookingTypes
} from "../../CompanyCard/SettingDialog/company-card-independent-bookings.config";

const initialCheckinAndMaxAdvancedBooking = (type: BookingType): CheckinAndMaxAdvancedBooking => ({
  bookingType: type,
  checkinBufferTime: 15,
  checkinWhenOpeningDoor: false,
  geoLocationRequired: false,
  maxWeeksInAdvance: 2,
  weekday: null,
  time: "",
  allowedBookingDaysInAdvance: null
});

const DEFAULT_COLOR = "##32D79B";

interface Props {
  createCompanyModalOpen: boolean;
  setCreateCompanyModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  enableNext: React.Dispatch<React.SetStateAction<boolean>> | undefined;
  /* callback function to be called when the company is created, refetch the metadata and companies */
  onConfirm: () => void;
}

export function CreateCompanyDialog({
  createCompanyModalOpen,
  setCreateCompanyModalOpen,
  enableNext,
  onConfirm
}: Readonly<Props>) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const {
    userRoles: { isTenantAdmin },
    userInformation: { placeTypes, zoneTypes, visitorManagementEnabled }
  } = useSelector((state: RootState) => state.login);

  const [newCompany, setNewCompany] = useState({ name: "", refId: uuidv4() });
  const [newValues, setNewValues] = useState(() => getDefaultMetaDataForCreate(DEFAULT_COLOR));
  const [logoFile, setLogoFile] = useState<FileObject[] | undefined>(undefined);
  const [homeOffice, setHomeOffice] = useState<{
    enabled: boolean;
    mandatory: boolean;
    homeOfficeDays: [number, number];
  }>({ homeOfficeDays: [0, 5], enabled: false, mandatory: false });

  const [checkinEnabledGrp, setCheckinEnabledGrp] = useState<CheckinPolicyGroup>({
    workplace: false,
    parkingplace: false,
    electricchargingplace: false,
    conferencezone: false,
    vehicle: false,
    visitor: false
  });

  const [wpSetting, setWpSetting] = useState<CheckinAndMaxAdvancedBooking>(
    initialCheckinAndMaxAdvancedBooking(BookingType.WORKPLACE)
  );
  const [parkPlSetting, setParkPlSetting] = useState<CheckinAndMaxAdvancedBooking>(
    initialCheckinAndMaxAdvancedBooking(BookingType.PARKINGPLACE)
  );
  const [eChargePlSetting, setEChargePlSetting] = useState<CheckinAndMaxAdvancedBooking>(
    initialCheckinAndMaxAdvancedBooking(BookingType.ELECTRICCHARGINGSTATIONPLACE)
  );
  const [confZnSetting, setConfZnSetting] = useState<CheckinAndMaxAdvancedBooking>(
    initialCheckinAndMaxAdvancedBooking(BookingType.CONFERENCEZONE)
  );
  const [vehicleSetting, setVehicleSetting] = useState<CheckinAndMaxAdvancedBooking>(
    initialCheckinAndMaxAdvancedBooking(BookingType.VEHICLE)
  );
  const [visitorSetting, setVisitorSetting] = useState<CheckinAndMaxAdvancedBooking>(
    initialCheckinAndMaxAdvancedBooking(BookingType.VISITOR)
  );

  const { mutateAsync: setMeta } = useRemoteSetMetadata({ mode: "Create" });
  const { refetch: refetchMetadata } = useRemoteFetchMetadata();

  async function handleClickCreate() {
    const dataAxios = await createCompany(newCompany);
    const companyBookingMetaId = (await refetchMetadata()).data?.find(
      d => d.companyId === dataAxios.data.id
    )?.id;

    const visibleIndependentBookingTypes = getVisibleIndependentBookingTypes({
      placeTypeIds: new Set(placeTypes.map(pt => pt.id)),
      zoneTypeIds: new Set(zoneTypes.map(zt => zt.id)),
      visitorManagementEnabled
    });

    const data = createFormMetadata({
      automatedWorkplaceBookingReflectionTime: 10,
      bookingNormHours: newValues.bookingNormHours ?? 8,
      brandColor: newValues.color ?? DEFAULT_COLOR,
      hidePastBookings: newValues.hidePastBookings,
      showBookingStatistics: newValues.showBookingStatistics,
      activateOfficeSafety: newValues.activateOfficeSafety,
      allowBookingCancellations: newValues.allowBookingCancellations,
      teamBookingAllowed: newValues.teamBookingAllowed,
      workplaceBookingEnabled: newValues.workplaceBookingEnabled ?? true,
      bookingsOnSaturdaysAllowed: newValues.bookingsOnSaturdaysAllowed ?? false,
      bookingsOnSundaysAllowed: newValues.bookingsOnSundaysAllowed ?? false,
      calendarSyncPossible: newValues.calendarSyncPossible,
      homeOffice,
      id: companyBookingMetaId || 1,
      isTenantAdmin,
      logoFile,
      noBookingTime: newValues.maxAllowedNoBookingTime ?? 20,
      createdCompanyId: dataAxios.data.id,
      bookingAssistenceActive: newValues.bookingAssistenceActive,
      checkinSettings: filterEnabledPolicy({
        checkinEnabledGrp,
        wpSetting,
        parkPlSetting,
        eChargePlSetting,
        confZnSetting,
        vehicleSetting,
        visitorSetting
      }),
      advancedBookingSettings: generateAdvancedBookingSettings(
        wpSetting,
        parkPlSetting,
        eChargePlSetting,
        confZnSetting,
        vehicleSetting,
        visitorSetting
      ),
      activateBookingServices: newValues.activateBookingServices,
      independentBookingSettings: buildIndependentBookingSettingsPayload(
        newValues.independentBookingSettings,
        visibleIndependentBookingTypes
      )
    });

    await setMeta(data)
      .then(() => {
        /** reset the form */
        resetForm();
        /** refetch the metadata and companies */
        onConfirm();
        if (enableNext) enableNext(true);
        /** close the modal */
        setCreateCompanyModalOpen(false);
      })
      .catch(() => enqueueSnackbar(t("Error setting metadata"), { variant: "error" }));
  }

  const resetForm = () => {
    resetCheckinSettings();
    setNewValues(getDefaultMetaDataForCreate(DEFAULT_COLOR));
    setLogoFile(undefined);
    setHomeOffice({ homeOfficeDays: [0, 5], enabled: false, mandatory: false });
  };

  const resetCheckinSettings = () => {
    setCheckinEnabledGrp({
      workplace: false,
      parkingplace: false,
      electricchargingplace: false,
      conferencezone: false,
      vehicle: false,
      visitor: false
    });
    setWpSetting(initialCheckinAndMaxAdvancedBooking(BookingType.WORKPLACE));
    setParkPlSetting(initialCheckinAndMaxAdvancedBooking(BookingType.PARKINGPLACE));
    setEChargePlSetting(
      initialCheckinAndMaxAdvancedBooking(BookingType.ELECTRICCHARGINGSTATIONPLACE)
    );
    setConfZnSetting(initialCheckinAndMaxAdvancedBooking(BookingType.CONFERENCEZONE));
    setVehicleSetting(initialCheckinAndMaxAdvancedBooking(BookingType.VEHICLE));
    setVisitorSetting(initialCheckinAndMaxAdvancedBooking(BookingType.VISITOR));
  };

  return (
    <Dialog
      open={createCompanyModalOpen}
      onClose={() => setCreateCompanyModalOpen(false)}
      maxWidth="xl"
      fullWidth
      slotProps={{ paper: { sx: { backgroundImage: "none" } } }}
      hideBackdrop
      data-testid="create-company-dialog"
      aria-labelledby="create-company-dialog-title"
    >
      <DialogContent>
        <CompanyCardSettingContent
          newValues={newValues}
          setNewValues={setNewValues}
          companyName={newCompany.name}
          brandColor={newValues.color ?? DEFAULT_COLOR}
          logoUrl={null}
          logoFile={logoFile}
          onEditLogo={() => {}}
          newSetting={{
            wpSetting,
            parkPlSetting,
            eChargePlSetting,
            confZnSetting,
            vehicleSetting,
            visitorSetting
          }}
          setNewSetting={{
            setWpSetting,
            setParkPlSetting,
            setEChargePlSetting,
            setConfZnSetting,
            setVehicleSetting,
            setVisitorSetting
          }}
          checkinEnabledGrp={checkinEnabledGrp}
          setCheckinEnabledGrp={setCheckinEnabledGrp}
          homeOffice={homeOffice}
          setHomeOffice={setHomeOffice}
          setCompanyName={name => setNewCompany(prev => ({ ...prev, name }))}
          setLogoFile={setLogoFile}
        />
      </DialogContent>
      <DialogActions>
        <Button
          color="secondary"
          onClick={() => setCreateCompanyModalOpen(false)}
          data-testid="create-company-dialog-cancel-btn"
        >
          {t("Cancel")}
        </Button>
        <Button data-testid="submitButton" disabled={!newCompany.name} onClick={handleClickCreate}>
          {t("Confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
